import {
  FILE_LIST_SORT_FIELDS,
  normalizeFileListFilters,
  normalizeFileListSort,
} from '../../config/fileListView';

function getTimestampMs(value) {
  if (!value) return null;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : null;
}

function hasFiniteNumber(value) {
  if (value === null || value === undefined || value === '') return false;
  const n = Number(value);
  return Number.isFinite(n);
}

export function getDateStatus(file) {
  const dateTaken = file?.modifiedExifData?.dateTaken;
  if (!dateTaken) return 'missing-date';
  return getTimestampMs(dateTaken) === null ? 'invalid-date' : 'with-date';
}

export function getGpsStatus(file) {
  const lat = file?.modifiedExifData?.latitude;
  const lon = file?.modifiedExifData?.longitude;
  return hasFiniteNumber(lat) && hasFiniteNumber(lon) ? 'with-gps' : 'missing-gps';
}

export function getEditStatus(file) {
  return file?.hasChanges ? 'edited' : 'unedited';
}

function compareWithMissingLast(left, right, compareValues) {
  const leftMissing = left === null || left === undefined;
  const rightMissing = right === null || right === undefined;

  if (leftMissing && rightMissing) return 0;
  if (leftMissing) return 1;
  if (rightMissing) return -1;

  return compareValues(left, right);
}

function compareText(left, right, direction) {
  const multiplier = direction === 'desc' ? -1 : 1;
  return left.localeCompare(right, undefined, { sensitivity: 'base', numeric: true }) * multiplier;
}

function compareNumber(left, right, direction) {
  return direction === 'desc' ? right - left : left - right;
}

export function fileMatchesFilters(file, filters) {
  const normalizedFilters = normalizeFileListFilters(filters);

  const dateStatus = getDateStatus(file);
  if (normalizedFilters.dateStatuses.length > 0 && !normalizedFilters.dateStatuses.includes(dateStatus)) {
    return false;
  }

  const gpsStatus = getGpsStatus(file);
  if (normalizedFilters.gpsStatuses.length > 0 && !normalizedFilters.gpsStatuses.includes(gpsStatus)) {
    return false;
  }

  const editStatus = getEditStatus(file);
  if (normalizedFilters.editStatuses.length > 0 && !normalizedFilters.editStatuses.includes(editStatus)) {
    return false;
  }

  return true;
}

export function compareFileIndices(files, leftIndex, rightIndex, sort) {
  const normalizedSort = normalizeFileListSort(sort);
  const leftFile = files[leftIndex];
  const rightFile = files[rightIndex];

  if (normalizedSort.field === FILE_LIST_SORT_FIELDS.IMPORT) {
    return normalizedSort.direction === 'desc' ? rightIndex - leftIndex : leftIndex - rightIndex;
  }

  let result = 0;

  if (normalizedSort.field === FILE_LIST_SORT_FIELDS.DATETIME) {
    const leftDate = getTimestampMs(leftFile?.modifiedExifData?.dateTaken);
    const rightDate = getTimestampMs(rightFile?.modifiedExifData?.dateTaken);
    result = compareWithMissingLast(leftDate, rightDate, (left, right) =>
      compareNumber(left, right, normalizedSort.direction),
    );
  }

  if (normalizedSort.field === FILE_LIST_SORT_FIELDS.FILENAME) {
    const leftName = leftFile?.name || '';
    const rightName = rightFile?.name || '';
    result = compareText(leftName, rightName, normalizedSort.direction);
  }

  if (normalizedSort.field === FILE_LIST_SORT_FIELDS.FILESIZE) {
    const leftSize = typeof leftFile?.size === 'number' ? leftFile.size : null;
    const rightSize = typeof rightFile?.size === 'number' ? rightFile.size : null;
    result = compareWithMissingLast(leftSize, rightSize, (left, right) =>
      compareNumber(left, right, normalizedSort.direction),
    );
  }

  if (result !== 0) {
    return result;
  }

  return leftIndex - rightIndex;
}

export function buildFileListProjection(files, sort, filters) {
  const allIndices = files.map((_, index) => index);

  const visibleIndices = allIndices
    .filter((index) => fileMatchesFilters(files[index], filters))
    .sort((leftIndex, rightIndex) => compareFileIndices(files, leftIndex, rightIndex, sort));

  return {
    visibleIndices,
    totalCount: files.length,
    visibleCount: visibleIndices.length,
  };
}
